import { Dropdown } from '@sunwebgroup/ui-components/uds'
import { isEmpty } from 'ramda'
import { use } from 'react'
import { useIntl } from '@/intl'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import {
  getFiltersSelector,
  getPricesSelector,
  mealplanFilterOptionsSelector,
} from '@/scopes/prices/components/selectors'
import { createPricesFilterClickEvent } from '@/scopes/prices/domain/analytics'
import { FiltersInPricesValues } from '@/scopes/prices/domain/filters/types'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'

function MealPlanFilter({ isFlightOnly = false }: { isFlightOnly: boolean }) {
  const intl = useIntl()
  const { selectFilter } = use(PricesActionsContext)
  const { isEmpty: isEmptyResponse } = usePricesSelector(getFiltersSelector)
  const { options, selectedValue } = usePricesSelector(mealplanFilterOptionsSelector)
  const { sendEvent } = useAnalytics()
  const { loading: isFetching } = usePricesSelector(getPricesSelector)
  const { isTablet } = useBreakpoints()
  const doNotRender
    = (isEmpty(options) && !isEmptyResponse) || (!isTablet && !isEmptyResponse && options.length <= 1) || isFlightOnly

  if (doNotRender) {
    return null
  }

  const setSelection = (selection: FiltersInPricesValues) => selectFilter({ type: 'Mealplan', selection })

  const handleFilterChange = (value?: string) => {
    if (value) {
      setSelection(value)
      sendEvent(
        createPricesFilterClickEvent({
          element: 'dropdown',
          type: 'boardType',
          result: value,
        }),
      )
    }
    else {
      setSelection(undefined)
    }
  }

  return (
    <Dropdown.Root
      value={selectedValue}
      onValueChange={handleFilterChange}
      disabled={isFetching || isEmptyResponse}
    >
      <Dropdown.Trigger
        placeholder={intl.formatMessage({ id: 'prices_filters_mealplan_placeholder' })}
        label={intl.formatMessage({ id: 'prices_filters_mealplan_label' })}
        disabled={isFetching || isEmptyResponse}
      />
      <Dropdown.Content>
        <Dropdown.EmptyValueItem disabled>{intl.formatMessage({ id: 'prices_filters_mealplan_placeholder' })}</Dropdown.EmptyValueItem>
        {options.map(({ name, value }) => (
          <Dropdown.Item key={value} value={value}>
            {name}
          </Dropdown.Item>
        ))}
      </Dropdown.Content>
    </Dropdown.Root>
  )
}

export { MealPlanFilter }
